/**
 * Upcoming Recurring Expenses Widget
 * 
 * Dashboard widget showing next recurring expenses due in the next 30 days.
 * Helps users anticipate upcoming auto-generated expenses.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RecurringExpenseService } from '@/lib/domains/recurring';
import type { RecurringExpense, CategoryViewModel } from '@/lib/domains';
import { formatCurrency } from '@/lib/utils/currency';
import { Calendar, Repeat, ChevronRight, Info } from 'lucide-react';
import { useCategories } from '@/hooks/useCategory';
import { UI_LIMITS } from '@/lib/constants';
import { handleError } from '@/lib/utils/error-handler';
import { Skeleton } from '@/components/skeleton';

export function UpcomingRecurringWidget() {
    const [upcoming, setUpcoming] = useState<RecurringExpense[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    // Load categories for all upcoming expenses
    const categoryIds = upcoming.map(r => r.categoryId).filter(Boolean) as string[];
    const { categories: categoryMap } = useCategories(categoryIds);

    useEffect(() => {
        loadUpcomingExpenses();
    }, []);

    const loadUpcomingExpenses = async () => {
        try {
            setLoading(true);

            const upcomingData = await RecurringExpenseService.getUpcomingRecurringExpenses(
                UI_LIMITS.UPCOMING_DAYS_WINDOW
            );

            setUpcoming(upcomingData.slice(0, UI_LIMITS.RECENT_EXPENSES_COUNT + 2)); // Show max 5
        } catch (err: unknown) {
            const errorMessage = handleError(err, 'Failed to load upcoming recurring expenses');
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const getCategoryInfo = (categoryId?: string): CategoryViewModel | null => {
        if (!categoryId) return null;
        return categoryMap.get(categoryId) || null;
    };

    const getDaysUntil = (dateStr: string): number => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(dateStr + 'T00:00:00');
        const diffTime = dueDate.getTime() - today.getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getDueDateLabel = (dateStr: string): string => {
        const days = getDaysUntil(dateStr);
        if (days === 0) return 'Today';
        if (days === 1) return 'Tomorrow';
        if (days < 0) return 'Overdue';
        if (days <= 7) return `In ${days} days`;
        return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    const getDueDateColor = (dateStr: string): string => {
        const days = getDaysUntil(dateStr);
        if (days < 0) return 'text-red-600';
        if (days === 0) return 'text-orange-600';
        if (days <= 3) return 'text-yellow-600';
        return 'text-gray-600';
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Repeat className="w-5 h-5 text-teal-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Upcoming Recurring</h2>
                </div>
                <div className="space-y-3 py-4">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Repeat className="w-5 h-5 text-teal-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Upcoming Recurring</h2>
                </div>
                <div className="text-center py-8 text-red-500">
                    Failed to load recurring expenses
                </div>
            </div>
        );
    }

    if (upcoming.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Repeat className="w-5 h-5 text-teal-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Upcoming Recurring</h2>
                </div>
                <div className="text-center py-8">
                    <p className="text-gray-500 mb-3">No recurring expenses set up</p>
                    <Link
                        to="/add"
                        className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
                    >
                        Create your first recurring expense →
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-teal-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Upcoming Recurring</h2>
                    <div className="relative">
                        <button
                            onClick={() => setShowTooltip(!showTooltip)}
                            onBlur={() => setTimeout(() => setShowTooltip(false), 200)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            aria-label="Information about recurring expenses"
                        >
                            <Info className="w-4 h-4 text-gray-400" />
                        </button>
                        {showTooltip && (
                            <div className="absolute left-0 top-8 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 z-10 shadow-lg">
                                These expenses will appear in your expense list on their due date, representing money already spent.
                                <div className="absolute -top-1 left-3 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                        )}
                    </div>
                </div>
                <Link
                    to="/settings/recurring"
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                >
                    Manage
                    <ChevronRight className="w-4 h-4" />
                </Link>
            </div>

            <div className="space-y-3">
                {upcoming.map((recurring) => {
                    const category = getCategoryInfo(recurring.categoryId);
                    const daysUntil = getDaysUntil(recurring.nextDueDate);

                    return (
                        <div
                            key={recurring.id}
                            className={`p-4 rounded-lg border transition-colors ${daysUntil < 0
                                ? 'border-red-200 bg-red-50'
                                : daysUntil === 0
                                    ? 'border-orange-200 bg-orange-50'
                                    : 'border-gray-200 bg-gray-50'
                                }`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 flex-1">
                                    {category && (
                                        <span className="text-2xl">{category.icon}</span>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 truncate">
                                            {category?.displayName || 'Other'}
                                        </div>
                                        {recurring.description && (
                                            <div className="text-sm text-gray-600 truncate">
                                                {recurring.description}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3 mt-1">
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                                <Calendar className="w-3 h-3" />
                                                {RecurringExpenseService.getFrequencyDisplayName(recurring.frequency)}
                                            </div>
                                            <div className={`text-xs font-medium ${getDueDateColor(recurring.nextDueDate)}`}>
                                                {getDueDateLabel(recurring.nextDueDate)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-semibold text-gray-900">
                                        {formatCurrency(recurring.amount, recurring.currency)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {upcoming.length >= 5 && (
                <Link
                    to="/settings/recurring"
                    className="mt-4 block text-center text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                    View all recurring expenses →
                </Link>
            )}
        </div>
    );
}
